import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  acceptInvite,
  attachInviteByCode,
  previewInvite,
  resolveInvite,
  sendEmailOTP,
  setSessionToken,
  signInWithEmailOTP,
} from "../api/client";
import { APIError, type InvitePreview } from "../api/types";
import { AttendWordmark, BrandMark, PortalFooter } from "../components/ui";

// /invite/:code?e=<email> — landing page from the invite email. Previews the
// invitation ("<Agency> invited you to view <Client> events"), then a single
// explicit click redeems it via POST /api/v1/invite/accept and stores the
// returned signed session token as the bearer.

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
const cleanEmail = (s: string) => s.trim().toLowerCase();

export function Invite() {
  const { code = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const emailFromUrl = params.get("e") ?? params.get("email") ?? "";
  const [email, setEmail] = useState(emailFromUrl);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otp, setOtp] = useState("");
  // Vendor invites have no portal home yet — accept stays on this route and
  // swaps to a success card instead of navigating to "/".
  const [accepted, setAccepted] = useState(false);

  async function loadPreview(addr: string) {
    setPreviewing(true);
    setError(null);
    setInvalid(false);
    try {
      setPreview(await previewInvite(code, cleanEmail(addr)));
    } catch (err) {
      if (err instanceof APIError && (err.kind === "not-found" || err.kind === "bad-request")) {
        setInvalid(true);
      } else {
        setError(err instanceof Error ? err.message : "Could not load this invitation");
      }
    } finally {
      setPreviewing(false);
    }
  }

  // Auto-preview when the email arrived on the link (the normal path).
  useEffect(() => {
    if (code && isValidEmail(emailFromUrl)) void loadPreview(emailFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onAccept() {
    setAccepting(true);
    setError(null);
    try {
      const resolved = await resolveInvite(code, cleanEmail(email));
      if (!resolved.valid || resolved.revoked || resolved.expired) {
        setInvalid(true);
        setAccepting(false);
        return;
      }
      if (resolved.bearerRefused) {
        await sendEmailOTP(cleanEmail(email));
        setOtpRequired(true);
        setAccepting(false);
        return;
      }
      const token = await acceptInvite(cleanEmail(email), code);
      setSessionToken(token);
      if (preview?.kind === "vendor") {
        setAccepted(true);
        setAccepting(false);
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not accept this invitation");
      setAccepting(false);
    }
  }

  async function onVerifyOtp() {
    if (otp.length !== 6 || accepting) return;
    setAccepting(true);
    setError(null);
    try {
      const token = await signInWithEmailOTP(cleanEmail(email), otp);
      setSessionToken(token);
      await attachInviteByCode(code);
      if (preview?.kind === "vendor") {
        setAccepted(true);
        setAccepting(false);
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wrong code — please try again");
      setOtp("");
      setAccepting(false);
    }
  }

  const accent = preview?.brand?.accentColor ?? "#D5B56F";

  return (
    <AuthFrame>
      {accepted ? (
        <Card>
          <CheckCircle2 size={28} className="text-emerald-600" />
          <div className="mt-3 text-[17px] font-semibold">You're all set.</div>
          <p className="mt-1.5 text-[14px] text-ink-soft">
            {preview?.agencyName ?? "Your event organizer"} has connected your account
            {preview?.vendorName ? ` as a contact for ${preview.vendorName}` : ""}.
            Event coordination tools are on the way — we'll email you when there's
            something to see.
          </p>
          <p className="mt-3 text-center text-[12px] text-ink-faint">
            Signed in as {cleanEmail(email)}
          </p>
        </Card>
      ) : invalid ? (
        <Card>
          <div className="text-[17px] font-semibold">This invitation isn't valid.</div>
          <p className="mt-1.5 text-[14px] text-ink-soft">
            The link may have expired, or the email address doesn't match the invitation.
            Check the email it was sent to, or sign in if you already have access.
          </p>
          <Link to="/sign-in" className="btn-primary mt-5 w-full">
            Go to sign in <ArrowRight size={16} />
          </Link>
        </Card>
      ) : preview ? (
        <Card>
          {preview.brand && (
            <div className="mb-5 flex items-center gap-2.5">
              <BrandMark brand={preview.brand} size="md" />
              {preview.brand.name && (
                <span className="text-[14px] font-medium text-ink-soft">{preview.brand.name}</span>
              )}
            </div>
          )}
          <div className="eyebrow">You're invited</div>
          {preview.kind === "vendor" ? (
            <>
              <h1 className="mt-2 font-serif text-[24px] leading-snug tracking-tight">
                {preview.agencyName ?? "Your event organizer"} added you as a contact for{" "}
                <span style={{ color: accent }}>{preview.vendorName}</span>.
              </h1>
              <p className="mt-2 text-[14px] text-ink-soft">
                Connect your account so they can coordinate events with you. Coordination
                tools are on the way.
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-2 font-serif text-[24px] leading-snug tracking-tight">
                {preview.agencyName ?? "Your event agency"} invited you to view{" "}
                <span style={{ color: accent }}>{preview.clientName}</span> events.
              </h1>
              <p className="mt-2 text-[14px] text-ink-soft">
                View-only access to {preview.accessScope === "all-events" ? "all" : "selected"} events —
                schedules, RSVP reports, and guest lists.
              </p>
            </>
          )}
          {otpRequired ? (
            <form
              className="mt-5 flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void onVerifyOtp();
              }}
            >
              <p className="text-[14px] text-ink-soft">
                We sent a 6-digit sign-in code to <span className="font-medium text-ink">{cleanEmail(email)}</span>.
              </p>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="••••••"
                className="field text-center text-[20px] tracking-[0.4em]"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
              />
              {error && <p className="text-[13px] text-rose-700">{error}</p>}
              <button type="submit" className="btn-primary w-full" disabled={otp.length !== 6 || accepting}>
                {accepting ? <Loader2 size={16} className="animate-spin" /> : <>Verify & continue <ArrowRight size={16} /></>}
              </button>
            </form>
          ) : (
            <>
              {error && <p className="mt-3 text-[13px] text-rose-700">{error}</p>}
              <button
                type="button"
                className="btn-primary mt-6 w-full"
                disabled={accepting}
                onClick={onAccept}
              >
                {accepting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : preview.kind === "vendor" ? (
                  <>Connect account <ArrowRight size={16} /></>
                ) : (
                  <>View events <ArrowRight size={16} /></>
                )}
              </button>
            </>
          )}
          <p className="mt-3 text-center text-[12px] text-ink-faint">
            Signing in as {cleanEmail(email)}
          </p>
        </Card>
      ) : (
        <Card>
          <div className="eyebrow">You're invited</div>
          <div className="mt-2 text-[17px] font-semibold">Confirm your email</div>
          <p className="mt-1.5 text-[14px] text-ink-soft">
            Enter the email address this invitation was sent to.
          </p>
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (isValidEmail(email)) void loadPreview(email);
            }}
          >
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              className="field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && <p className="text-[13px] text-rose-700">{error}</p>}
            <button type="submit" className="btn-primary" disabled={!isValidEmail(email) || previewing}>
              {previewing ? <Loader2 size={16} className="animate-spin" /> : <>Continue <ArrowRight size={16} /></>}
            </button>
          </form>
        </Card>
      )}
    </AuthFrame>
  );
}

export function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col px-5">
      {/* The auth card owns the full viewport; the footer sits below the fold. */}
      <div className="flex min-h-dvh flex-col items-center justify-center py-10">
        <div className="mb-8 flex flex-col items-center gap-2">
          <AttendWordmark className="h-4" />
          <span className="eyebrow">Client portal</span>
        </div>
        <div className="w-full max-w-md">{children}</div>
      </div>
      <PortalFooter className="mx-auto w-full max-w-5xl pb-6" />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="luxe-panel rounded-card p-7 shadow-lift">{children}</div>
  );
}
